import {Component, h} from "preact";
import interact from "interactjs";
import {getStyle} from "mojave/dom/css";
import {classes} from "mojave/classes";

/* eslint-disable jsdoc/valid-types */

/**
 * @typedef {{
 *      previewUrl: string,
 *      markers: MarkerPositioning.Marker[]
 *      onMarkerPositioned: function(MarkerPositioning.Marker, number, number),
 *      hovered: ?MarkerPositioning.Marker,
 *      onHoverEnter: function(MarkerPositioning.Marker),
 *      onHoverLeave: function(),
 * }} MarkerPreview.Props
 *
 * @typedef {{
 * }} MarkerPreview.State
 */
export class MarkerPreview extends Component
{
	/**
	 * @private
	 * @type {number}
	 */
	height = 1;


	/**
	 * @private
	 * @type {number}
	 */
	width = 1;


	/**
	 * @private
	 * @type {?{x: number, y: number}}
	 */
	currentOffset;


	/**
	 * @private
	 * @type {WeakMap}
	 */
	markerMap;


	/**
	 * @inheritDoc
	 * @param {MarkerPreview.Props} props
	 */
	constructor (props)
	{
		super(props);
		this.markerMap = new window.WeakMap();
	}


	/**
	 * @param {MarkerPreview.Props} props
	 * @returns {null|JSX.Element}
	 */
	render (props)
	{
		return (
			<div className="marker-positioning-preview" style={`background-image: url("${props.previewUrl}")`}>
				{props.markers.map(marker => this.renderMarker(marker))}
			</div>
		);
	}


	/**
	 * @inheritDoc
	 */
	componentDidMount ()
	{
		this.width = getStyle(this.base, "width");
		this.height = getStyle(this.base, "height");

		// initialize draggable
		interact('.preview-placeholder', {
			context: this.base,
		})
			.draggable({
				restrict: {
					restriction: "parent",
				},
				onstart: () => this.onDragStart(),
				onmove: (event) => this.onMarkerMove(event),
				onend: (event) => this.onMarkerDrop(event),
			});
	}

	onDragStart ()
	{
		this.currentOffset = {
			x: 0,
			y: 0,
		};
	}


	/**
	 * Callback on when the marker is moved
	 *
	 * @private
	 * @param {{dx: number, dy: number, target: HTMLElement}} event
	 */
	onMarkerMove (event)
	{
		this.currentOffset.x += event.dx;
		this.currentOffset.y += event.dy;
		event.target.style.transform = `translate(${this.currentOffset.x}px, ${this.currentOffset.y}px)`;
	}


	/**
	 * @private
	 * @param {{target: HTMLElement}} event
	 */
	onMarkerDrop (event)
	{
		let element = event.target;
		let style = element.style;

		// fetch old position
		let left = parseFloat(style.left.replace("%", ""), 10);
		let top = parseFloat(style.top.replace("%", ""), 10);

		// add new offsets (and transform them to percent before)
		left += 100 * (this.currentOffset.x / this.width);
		top += 100 * (this.currentOffset.y / this.height);

		// reset state
		this.currentOffset = null;
		element.style.transform = `translate(0,0)`;

		// update marker
		this.props.onMarkerPositioned(this.markerMap.get(element), left, top);
	}


	/**
	 * Renders a single marker
	 *
	 * @private
	 * @param {MarkerPositioning.Marker} marker
	 * @returns {?JSX.Element}
	 */
	renderMarker (marker)
	{
		if (null === marker.x)
		{
			return null;
		}

		/** @type {MarkerPreview.Props} props */
		const props = this.props;

		return (
			<div
				class={classes({
					"preview-placeholder": true,
					[`placeholder-type-${marker.type}`]: true,
					"is-hovered": props.hovered === marker,
				})}
				style={{
					left: `${marker.x}%`,
					top: `${marker.y}%`,
				}}
				ref={el =>
				{
					if (null !== el)
					{
						this.markerMap.set(el, marker);
					}
				}}
				title={marker.label}
				onMouseEnter={() => props.onHoverEnter(marker)}
				onMouseLeave={() => props.onHoverLeave()}
			>
				{marker.placeholderContent}
			</div>
		);
	}
}
